import { dividedBy, times, type Money } from "../money/exact";
/**
 * Moving stock between branches, in two steps: sent, then received.
 *
 * What was sent leaves the sending branch at once. What arrives is counted at
 * the other end; if fewer arrived, the difference is recorded with a reason —
 * never quietly corrected — so somebody can answer for it.
 */

/** Why fewer arrived than were sent. "other" needs words. */
export const SHORTFALL_REASONS = ["missing", "damaged", "other"] as const;
export type ShortfallReason = (typeof SHORTFALL_REASONS)[number];

export type ReceiveProblem =
  | { code: "not_a_number" }
  | { code: "more_than_sent" }
  /** Fewer arrived: why? */
  | { code: "reason_needed" }
  /** "Other" says what happened. */
  | { code: "note_needed" };

/** One medicine counted in at the receiving branch. */
export function checkReceivedLine(input: {
  sentBase: number;
  receivedBase: number;
  reason: ShortfallReason | null;
  note: string | null;
}): ReceiveProblem[] {
  const { sentBase, receivedBase, reason, note } = input;
  if (!Number.isInteger(receivedBase) || receivedBase < 0) return [{ code: "not_a_number" }];
  if (receivedBase > sentBase) return [{ code: "more_than_sent" }];
  if (receivedBase === sentBase) return [];
  if (!reason) return [{ code: "reason_needed" }];
  if (reason === "other" && !note?.trim()) return [{ code: "note_needed" }];
  return [];
}

/* ---------------------------------------------------- counting in units */

/** A medicine as far as counting goes: its smallest unit, and its sizes. */
export interface Counted {
  baseUnit: string;
  sizes: ReadonlyArray<{ name: string; multiplier: number }>;
}

/** How many of one medicine's smallest unit make one of another's: a fraction. */
export interface UnitRatio {
  num: number;
  den: number;
}

/** A unit's word, for matching: "Strip (10)", "strips" and "strip" are one. */
export function unitWord(name: string): string {
  const word = name.trim().toLowerCase().replace(/\s+/g, " ").replace(/\s*\(\d+\)$/, "").trim();
  if (/(s|x|z|ch|sh)es$/.test(word) && word.length > 4) return word.slice(0, -2);
  if (word.endsWith("s") && !word.endsWith("ss") && word.length > 2) return word.slice(0, -1);
  return word;
}

/**
 * How one of `from`'s smallest unit counts in `to`'s — so a medicine sent
 * counted in tablets can be received into one counted in strips of 10, as the
 * owner asked. The same unit is 1; otherwise one of them must sell a size that
 * is the other's smallest unit, which says how many make one. Null when
 * nothing says how the two are related — then they cannot be matched.
 *
 * Tablets into strips of 10: 1/10. Strips of 10 into tablets: 10.
 */
export function unitRatio(from: Counted, to: Counted): UnitRatio | null {
  const fromWord = unitWord(from.baseUnit);
  const toWord = unitWord(to.baseUnit);
  if (fromWord === toWord) return { num: 1, den: 1 };

  const toSellsFrom = to.sizes.find((size) => size.multiplier > 1 && unitWord(size.name) === fromWord);
  if (toSellsFrom) return { num: toSellsFrom.multiplier, den: 1 };

  const fromSellsTo = from.sizes.find((size) => size.multiplier > 1 && unitWord(size.name) === toWord);
  if (fromSellsTo) return { num: 1, den: fromSellsTo.multiplier };

  return null;
}

/** A quantity in the other medicine's smallest unit — or null when it is not a whole number of them. */
export function convertQty(qty: number, ratio: UnitRatio): number | null {
  const scaled = qty * ratio.num;
  return scaled % ratio.den === 0 ? scaled / ratio.den : null;
}

/**
 * What one of the other medicine's smallest unit cost, from what one of this
 * one's did: exact, or cut at the tenth decimal where it does not end.
 */
export function convertCost(costPerUnit: Money, ratio: UnitRatio): Money {
  return dividedBy(times(costPerUnit, ratio.den), ratio.num);
}

/* ------------------------------------------------ changing it afterwards */

/** How many days after it was received its quantities can still be corrected. */
export const TRANSFER_CORRECT_DAYS = 7;

/**
 * Whether a received transfer's quantities can still be corrected: for a
 * week after the day it was received, as the owner decided. Days are the
 * pharmacy's own, YYYY-MM-DD.
 */
export function canCorrectTransfer(receivedOn: string, today: string): boolean {
  const days = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${receivedOn}T00:00:00Z`)) / 86_400_000);
  return days >= 0 && days <= TRANSFER_CORRECT_DAYS;
}
